/**
 * Post Studio Canvas Templates
 * - BACKGROUNDS: Visual canvas backgrounds (always randomized per generation)
 * - BASE DESIGNS: Text layout compositions (selectable poster styles)
 */

// ── Helpers ─────────────────────────────────────────────

function rng() {
  return Math.random();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? line + ' ' + word : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawAccentText(ctx, line, x, y, accentWord, accentColor) {
  if (accentWord && line.includes(accentWord)) {
    const idx = line.indexOf(accentWord);
    const before = line.substring(0, idx);
    const after = line.substring(idx + accentWord.length);
    let xPos = x;
    if (before) { ctx.fillStyle = '#FFFFFF'; ctx.fillText(before, xPos, y); xPos += ctx.measureText(before).width; }
    ctx.fillStyle = accentColor;
    ctx.fillText(accentWord, xPos, y);
    xPos += ctx.measureText(accentWord).width;
    if (after) { ctx.fillStyle = '#FFFFFF'; ctx.fillText(after, xPos, y); }
  } else {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(line, x, y);
  }
}

function drawBrandingBar(ctx, size, pad, textArea, baseFontSize) {
  const barY = size.h - pad;
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fillRect(pad, barY - 1, textArea, 1);
  ctx.font = `600 ${baseFontSize * 0.24}px 'Space Grotesk', sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.fillText('VISUALTE', pad, barY + baseFontSize * 0.15);
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.font = `400 ${baseFontSize * 0.2}px 'IBM Plex Sans', sans-serif`;
  const vizText = 'visualte.com';
  ctx.fillText(vizText, size.w - pad - ctx.measureText(vizText).width, barY + baseFontSize * 0.15);
}


// ═══════════════════════════════════════════════════════
//  BACKGROUNDS — Always randomized, never the same twice
// ═══════════════════════════════════════════════════════

function drawBgMinimalDark(ctx, size) {
  const bg = ctx.createLinearGradient(0, 0, size.w, size.h);
  bg.addColorStop(0, '#070A0E');
  bg.addColorStop(0.5, '#0D1117');
  bg.addColorStop(1, '#070A0E');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, size.w, size.h);

  const gx = size.w * (0.2 + rng() * 0.3);
  const gy = size.h * (0.2 + rng() * 0.3);
  const glow = ctx.createRadialGradient(gx, gy, 0, gx, gy, size.w * 0.6);
  glow.addColorStop(0, `rgba(232, 114, 42, ${0.04 + rng() * 0.04})`);
  glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size.w, size.h);

  ctx.fillStyle = 'rgba(255,255,255,0.03)';
  const spacing = 30 + Math.floor(rng() * 20);
  for (let x = 0; x < size.w; x += spacing) {
    for (let y = 0; y < size.h; y += spacing) {
      ctx.beginPath(); ctx.arc(x, y, 1, 0, Math.PI * 2); ctx.fill();
    }
  }
}

function drawBgEmberForge(ctx, size) {
  const bg = ctx.createLinearGradient(0, 0, size.w * 0.5, size.h);
  bg.addColorStop(0, '#0A0404');
  bg.addColorStop(0.4, '#120808');
  bg.addColorStop(1, '#0A0404');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, size.w, size.h);

  // Molten core — random position each time
  const cx = size.w * (0.15 + rng() * 0.7);
  const cy = size.h * (0.1 + rng() * 0.4);
  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, size.w * (0.4 + rng() * 0.2));
  core.addColorStop(0, `rgba(255, 100, 20, ${0.10 + rng() * 0.08})`);
  core.addColorStop(0.3, 'rgba(200, 60, 10, 0.05)');
  core.addColorStop(1, 'transparent');
  ctx.fillStyle = core;
  ctx.fillRect(0, 0, size.w, size.h);

  // Ember orbs — random count, size, position every time
  const orbCount = 5 + Math.floor(rng() * 8);
  for (let i = 0; i < orbCount; i++) {
    const ox = rng() * size.w;
    const oy = rng() * size.h * 0.7;
    const or2 = 15 + rng() * (size.w * 0.09);
    const orbG = ctx.createRadialGradient(ox, oy, 0, ox, oy, or2);
    orbG.addColorStop(0, `rgba(255, ${100 + Math.floor(rng() * 100)}, ${10 + Math.floor(rng() * 50)}, ${0.10 + rng() * 0.10})`);
    orbG.addColorStop(0.5, `rgba(200, 60, 10, ${0.03 + rng() * 0.03})`);
    orbG.addColorStop(1, 'transparent');
    ctx.fillStyle = orbG;
    ctx.fillRect(ox - or2, oy - or2, or2 * 2, or2 * 2);
  }

  // Particles
  const particleCount = 30 + Math.floor(rng() * 30);
  for (let i = 0; i < particleCount; i++) {
    ctx.fillStyle = `rgba(255, ${120 + Math.floor(rng() * 60)}, 40, ${0.08 + rng() * 0.12})`;
    ctx.beginPath();
    ctx.arc(rng() * size.w, rng() * size.h, 0.8 + rng() * 3, 0, Math.PI * 2);
    ctx.fill();
  }

  // Heat haze
  ctx.strokeStyle = `rgba(255, 100, 30, ${0.02 + rng() * 0.03})`;
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 4 + Math.floor(rng() * 4); i++) {
    const startY = size.h * (0.1 + rng() * 0.6);
    const amp = 15 + rng() * 35;
    const freq = 0.003 + rng() * 0.004;
    const phase = rng() * Math.PI * 6;
    ctx.beginPath(); ctx.moveTo(0, startY);
    for (let x = 0; x < size.w; x += 10) ctx.lineTo(x, startY + Math.sin(x * freq + phase) * amp);
    ctx.stroke();
  }
}

function drawBgCircuitPulse(ctx, size) {
  const bg = ctx.createLinearGradient(0, 0, size.w, size.h);
  bg.addColorStop(0, '#050810');
  bg.addColorStop(0.5, '#080C18');
  bg.addColorStop(1, '#050810');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, size.w, size.h);

  const gridSize = 40 + Math.floor(rng() * 30);
  const accent = [232, 114, 42];

  ctx.strokeStyle = `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, 0.035)`;
  ctx.lineWidth = 1;
  for (let x = 0; x <= size.w; x += gridSize) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, size.h); ctx.stroke(); }
  for (let y = 0; y <= size.h; y += gridSize) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(size.w, y); ctx.stroke(); }

  // Random circuit paths
  ctx.lineWidth = 2;
  for (let i = 0; i < 8 + Math.floor(rng() * 8); i++) {
    ctx.strokeStyle = `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, ${0.04 + rng() * 0.06})`;
    ctx.beginPath();
    let px = Math.floor(rng() * (size.w / gridSize)) * gridSize;
    let py = Math.floor(rng() * (size.h / gridSize)) * gridSize;
    ctx.moveTo(px, py);
    for (let s = 0; s < 4 + Math.floor(rng() * 4); s++) {
      if (rng() > 0.5) px += (rng() > 0.5 ? 1 : -1) * gridSize * (1 + Math.floor(rng() * 5));
      else py += (rng() > 0.5 ? 1 : -1) * gridSize * (1 + Math.floor(rng() * 5));
      px = Math.max(0, Math.min(size.w, px));
      py = Math.max(0, Math.min(size.h, py));
      ctx.lineTo(px, py);
    }
    ctx.stroke();
  }

  // Glowing nodes
  for (let i = 0; i < 12 + Math.floor(rng() * 10); i++) {
    const nx = Math.floor(rng() * (size.w / gridSize)) * gridSize;
    const ny = Math.floor(rng() * (size.h / gridSize)) * gridSize;
    const nr = 3 + rng() * 9;
    const nodeG = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr * 3);
    nodeG.addColorStop(0, `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, ${0.15 + rng() * 0.15})`);
    nodeG.addColorStop(0.4, `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, 0.04)`);
    nodeG.addColorStop(1, 'transparent');
    ctx.fillStyle = nodeG;
    ctx.fillRect(nx - nr * 3, ny - nr * 3, nr * 6, nr * 6);
    ctx.fillStyle = `rgba(255, 200, 150, ${0.25 + rng() * 0.2})`;
    ctx.beginPath(); ctx.arc(nx, ny, nr * 0.35, 0, Math.PI * 2); ctx.fill();
  }
}

function drawBgNebulaDrift(ctx, size) {
  ctx.fillStyle = '#040208';
  ctx.fillRect(0, 0, size.w, size.h);

  const nebColors = [[180,80,220],[232,114,42],[60,120,220],[200,50,80],[100,180,160]];
  for (let i = 0; i < 4 + Math.floor(rng() * 3); i++) {
    const nc = nebColors[Math.floor(rng() * nebColors.length)];
    const nx = rng() * size.w; const ny = rng() * size.h * 0.7;
    const nr = size.w * (0.2 + rng() * 0.4);
    const g = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr);
    g.addColorStop(0, `rgba(${nc[0]},${nc[1]},${nc[2]},${0.05 + rng() * 0.06})`);
    g.addColorStop(0.5, `rgba(${nc[0]},${nc[1]},${nc[2]},${0.01 + rng() * 0.02})`);
    g.addColorStop(1, 'transparent');
    ctx.fillStyle = g; ctx.fillRect(0, 0, size.w, size.h);
  }

  // Stars — random each time
  for (let i = 0; i < 60 + Math.floor(rng() * 50); i++) {
    ctx.fillStyle = `rgba(255,255,255,${0.2 + rng() * 0.7})`;
    ctx.beginPath(); ctx.arc(rng() * size.w, rng() * size.h, 0.4 + rng() * 2, 0, Math.PI * 2); ctx.fill();
  }

  // Shiny orbs
  for (let i = 0; i < 3 + Math.floor(rng() * 3); i++) {
    const ox = rng() * size.w; const oy = rng() * size.h * 0.6;
    const or2 = size.w * (0.03 + rng() * 0.06);
    const oc = nebColors[Math.floor(rng() * nebColors.length)];
    const glow = ctx.createRadialGradient(ox, oy, 0, ox, oy, or2 * 4);
    glow.addColorStop(0, `rgba(${oc[0]},${oc[1]},${oc[2]},0.12)`);
    glow.addColorStop(0.5, `rgba(${oc[0]},${oc[1]},${oc[2]},0.03)`);
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow; ctx.fillRect(ox - or2*4, oy - or2*4, or2*8, or2*8);
    const coreG = ctx.createRadialGradient(ox - or2*0.3, oy - or2*0.3, 0, ox, oy, or2);
    coreG.addColorStop(0, 'rgba(255,255,255,0.25)');
    coreG.addColorStop(0.4, `rgba(${oc[0]},${oc[1]},${oc[2]},0.2)`);
    coreG.addColorStop(1, `rgba(${oc[0]},${oc[1]},${oc[2]},0.05)`);
    ctx.fillStyle = coreG; ctx.beginPath(); ctx.arc(ox, oy, or2, 0, Math.PI * 2); ctx.fill();
  }
}

function drawBgGridMatrix(ctx, size) {
  ctx.fillStyle = '#060A0F';
  ctx.fillRect(0, 0, size.w, size.h);

  const vpx = size.w * (0.3 + rng() * 0.4);
  const vpy = size.h * (0.15 + rng() * 0.2);
  const vpGlow = ctx.createRadialGradient(vpx, vpy, 0, vpx, vpy, size.w * 0.5);
  vpGlow.addColorStop(0, `rgba(232,114,42,${0.06 + rng() * 0.06})`);
  vpGlow.addColorStop(0.5, 'rgba(232,114,42,0.02)');
  vpGlow.addColorStop(1, 'transparent');
  ctx.fillStyle = vpGlow; ctx.fillRect(0, 0, size.w, size.h);

  ctx.strokeStyle = 'rgba(232,114,42,0.05)'; ctx.lineWidth = 1;
  const numLines = 16 + Math.floor(rng() * 10);
  for (let i = 0; i < numLines; i++) {
    const angle = (i / numLines) * Math.PI * 2;
    ctx.beginPath(); ctx.moveTo(vpx, vpy);
    ctx.lineTo(vpx + Math.cos(angle) * size.w * 1.5, vpy + Math.sin(angle) * size.h * 1.5);
    ctx.stroke();
  }
  const ringSpacing = 60 + Math.floor(rng() * 40);
  for (let r = ringSpacing; r < size.w; r += ringSpacing) {
    ctx.strokeStyle = `rgba(232,114,42,${0.02 + 0.02 * Math.sin(r * 0.01 + rng())})`;
    ctx.beginPath(); ctx.arc(vpx, vpy, r, 0, Math.PI * 2); ctx.stroke();
  }

  for (let i = 0; i < 20 + Math.floor(rng() * 15); i++) {
    ctx.fillStyle = `rgba(232,114,42,${0.03 + rng() * 0.06})`;
    ctx.fillRect(rng() * size.w, rng() * size.h, 3 + rng() * 20, 1.5 + rng() * 5);
  }

  for (let i = 0; i < 4 + Math.floor(rng() * 4); i++) {
    const ox = rng() * size.w; const oy = rng() * size.h * 0.5;
    const or2 = 10 + rng() * 25;
    const g = ctx.createRadialGradient(ox, oy, 0, ox, oy, or2);
    g.addColorStop(0, `rgba(232,114,42,${0.08 + rng() * 0.1})`);
    g.addColorStop(0.6, 'rgba(232,114,42,0.02)'); g.addColorStop(1, 'transparent');
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(ox, oy, or2, 0, Math.PI * 2); ctx.fill();
  }
}

function drawBgAuroraWave(ctx, size) {
  const bg = ctx.createLinearGradient(0, 0, 0, size.h);
  bg.addColorStop(0, '#030812'); bg.addColorStop(0.5, '#060D1A'); bg.addColorStop(1, '#030812');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, size.w, size.h);

  const bands = [
    { color: [40,200,180], yBase: 0.1 + rng() * 0.1 },
    { color: [232,114,42], yBase: 0.18 + rng() * 0.1 },
    { color: [60,140,220], yBase: 0.25 + rng() * 0.1 },
    { color: [180,80,200], yBase: 0.08 + rng() * 0.1 },
  ];
  for (const band of bands) {
    const yBase = size.h * band.yBase;
    const amp = 25 + rng() * 55;
    const freq = 0.002 + rng() * 0.005;
    const phase = rng() * Math.PI * 6;
    for (let pass = 0; pass < 3; pass++) {
      ctx.fillStyle = `rgba(${band.color[0]},${band.color[1]},${band.color[2]},${0.035 - pass * 0.008})`;
      ctx.beginPath(); ctx.moveTo(0, size.h);
      for (let x = 0; x <= size.w; x += 4) ctx.lineTo(x, yBase + Math.sin(x * freq + phase + pass * 0.5) * amp + pass * 20);
      ctx.lineTo(size.w, size.h); ctx.closePath(); ctx.fill();
    }
  }

  for (let i = 0; i < 40 + Math.floor(rng() * 30); i++) {
    ctx.fillStyle = `rgba(255,255,255,${0.15 + rng() * 0.5})`;
    ctx.beginPath(); ctx.arc(rng() * size.w, rng() * size.h * 0.55, 0.4 + rng() * 1.5, 0, Math.PI * 2); ctx.fill();
  }
}


// ═══════════════════════════════════════════════════════
//  BASE DESIGNS — Text layout / poster composition styles
// ═══════════════════════════════════════════════════════

function layoutClassic(ctx, content, size, accentColor) {
  const pad = size.w * 0.08;
  const textArea = size.w - pad * 2;
  const isVert = size.h > size.w;
  const base = isVert ? size.w * 0.075 : size.w * 0.055;
  ctx.textBaseline = 'top';

  // Headline — left-aligned, middle area
  ctx.font = `700 ${base}px 'Space Grotesk', 'Arial Black', sans-serif`;
  const hLines = wrapText(ctx, content.headline || 'Power Engine', textArea);
  const hY = isVert ? size.h * 0.38 : size.h * 0.32;
  hLines.forEach((line, i) => drawAccentText(ctx, line, pad, hY + i * base * 1.15, content.accent_word, accentColor));

  // Body — below headline
  ctx.font = `400 ${base * 0.42}px 'IBM Plex Sans', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  const bLines = wrapText(ctx, content.body || '', textArea);
  const bY = hY + hLines.length * base * 1.15 + base * 0.6;
  bLines.forEach((line, i) => ctx.fillText(line, pad, bY + i * base * 0.42 * 1.6));

  // Tagline
  if (content.tagline) {
    ctx.font = `500 ${base * 0.28}px 'IBM Plex Mono', monospace`;
    ctx.fillStyle = accentColor + 'B3';
    ctx.fillText(content.tagline.toUpperCase(), pad, size.h - pad - base * 0.28 * 3);
  }
  drawBrandingBar(ctx, size, pad, textArea, base);
}

function layoutHeroStatement(ctx, content, size, accentColor) {
  const pad = size.w * 0.08;
  const textArea = size.w - pad * 2;
  const isVert = size.h > size.w;
  const base = isVert ? size.w * 0.095 : size.w * 0.07;
  ctx.textBaseline = 'top';

  // Huge centered headline
  ctx.font = `800 ${base}px 'Space Grotesk', 'Arial Black', sans-serif`;
  ctx.textAlign = 'center';
  const hLines = wrapText(ctx, (content.headline || 'Power Engine').toUpperCase(), textArea);
  const totalH = hLines.length * base * 1.1;
  const hY = (size.h - totalH) * 0.42;
  hLines.forEach((line, i) => {
    drawAccentText(ctx, line, size.w / 2 - ctx.measureText(line).width / 2, hY + i * base * 1.1, content.accent_word?.toUpperCase(), accentColor);
  });

  // Body — centered below
  ctx.font = `400 ${base * 0.35}px 'IBM Plex Sans', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  const bLines = wrapText(ctx, content.body || '', textArea * 0.8);
  const bY = hY + totalH + base * 0.5;
  bLines.forEach((line, i) => ctx.fillText(line, (size.w - ctx.measureText(line).width) / 2, bY + i * base * 0.35 * 1.6));

  // Tagline centered
  ctx.textAlign = 'left';
  if (content.tagline) {
    ctx.font = `600 ${base * 0.22}px 'IBM Plex Mono', monospace`;
    ctx.fillStyle = accentColor + '99';
    const tagText = content.tagline.toUpperCase();
    ctx.fillText(tagText, (size.w - ctx.measureText(tagText).width) / 2, bY + bLines.length * base * 0.35 * 1.6 + base * 0.4);
  }
  drawBrandingBar(ctx, size, pad, textArea, base * 0.75);
}

function layoutEditorial(ctx, content, size, accentColor) {
  const pad = size.w * 0.1;
  const textArea = size.w - pad * 2;
  const isVert = size.h > size.w;
  const base = isVert ? size.w * 0.065 : size.w * 0.05;
  ctx.textBaseline = 'top';

  // Kicker / tagline at top
  if (content.tagline) {
    ctx.font = `500 ${base * 0.32}px 'IBM Plex Mono', monospace`;
    ctx.fillStyle = accentColor + 'AA';
    ctx.fillText(content.tagline.toUpperCase(), pad, pad);
  }

  // Thin accent line
  ctx.fillStyle = accentColor + '40';
  ctx.fillRect(pad, pad + base * 0.5, textArea * 0.2, 2);

  // Headline — elegant, upper area
  ctx.font = `600 ${base}px 'Space Grotesk', 'Arial Black', sans-serif`;
  const hLines = wrapText(ctx, content.headline || 'Power Engine', textArea);
  const hY = isVert ? size.h * 0.2 : size.h * 0.18;
  hLines.forEach((line, i) => drawAccentText(ctx, line, pad, hY + i * base * 1.2, content.accent_word, accentColor));

  // Body — generous spacing in middle
  ctx.font = `300 ${base * 0.45}px 'IBM Plex Sans', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  const bLines = wrapText(ctx, content.body || '', textArea * 0.85);
  const bY = hY + hLines.length * base * 1.2 + base * 1;
  bLines.forEach((line, i) => ctx.fillText(line, pad, bY + i * base * 0.45 * 1.8));

  drawBrandingBar(ctx, size, pad, textArea, base);
}

function layoutBoldStack(ctx, content, size, accentColor) {
  const pad = size.w * 0.08;
  const textArea = size.w - pad * 2;
  const isVert = size.h > size.w;
  const base = isVert ? size.w * 0.085 : size.w * 0.065;
  ctx.textBaseline = 'top';

  // Headline — large, tightly stacked
  ctx.font = `900 ${base}px 'Space Grotesk', 'Arial Black', sans-serif`;
  const headline = (content.headline || 'Power Engine').toUpperCase();
  const hLines = wrapText(ctx, headline, textArea);
  const hY = isVert ? size.h * 0.28 : size.h * 0.25;

  // Background highlight bar behind each headline line
  hLines.forEach((line, i) => {
    const y = hY + i * base * 1.05;
    const lineW = ctx.measureText(line).width;
    ctx.fillStyle = accentColor + '18';
    ctx.fillRect(pad - 8, y - 4, lineW + 16, base + 8);
    drawAccentText(ctx, line, pad, y, content.accent_word?.toUpperCase(), accentColor);
  });

  // Body — compact below
  ctx.font = `500 ${base * 0.36}px 'IBM Plex Sans', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  const bLines = wrapText(ctx, content.body || '', textArea);
  const bY = hY + hLines.length * base * 1.05 + base * 0.7;
  bLines.forEach((line, i) => ctx.fillText(line, pad, bY + i * base * 0.36 * 1.5));

  // Tagline with accent dot
  if (content.tagline) {
    const tagY = bY + bLines.length * base * 0.36 * 1.5 + base * 0.5;
    ctx.fillStyle = accentColor;
    ctx.beginPath(); ctx.arc(pad + 4, tagY + base * 0.15, 4, 0, Math.PI * 2); ctx.fill();
    ctx.font = `600 ${base * 0.26}px 'IBM Plex Mono', monospace`;
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText(content.tagline.toUpperCase(), pad + 16, tagY);
  }
  drawBrandingBar(ctx, size, pad, textArea, base * 0.8);
}

function layoutCinematic(ctx, content, size, accentColor) {
  const pad = size.w * 0.08;
  const textArea = size.w - pad * 2;
  const isVert = size.h > size.w;
  const base = isVert ? size.w * 0.07 : size.w * 0.055;
  ctx.textBaseline = 'top';

  // Everything in the bottom third — like a movie poster
  const bottomStart = size.h * (isVert ? 0.58 : 0.52);

  // Subtle top-down dark gradient to push focus to bottom
  const vignette = ctx.createLinearGradient(0, 0, 0, size.h);
  vignette.addColorStop(0, 'transparent');
  vignette.addColorStop(0.5, 'transparent');
  vignette.addColorStop(0.85, 'rgba(0,0,0,0.3)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.5)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, size.w, size.h);

  // Headline at bottom area
  ctx.font = `700 ${base}px 'Space Grotesk', 'Arial Black', sans-serif`;
  const hLines = wrapText(ctx, content.headline || 'Power Engine', textArea);
  hLines.forEach((line, i) => drawAccentText(ctx, line, pad, bottomStart + i * base * 1.15, content.accent_word, accentColor));

  // Body
  ctx.font = `400 ${base * 0.4}px 'IBM Plex Sans', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  const bLines = wrapText(ctx, content.body || '', textArea);
  const bY = bottomStart + hLines.length * base * 1.15 + base * 0.5;
  bLines.forEach((line, i) => ctx.fillText(line, pad, bY + i * base * 0.4 * 1.6));

  // Tagline
  if (content.tagline) {
    ctx.font = `500 ${base * 0.26}px 'IBM Plex Mono', monospace`;
    ctx.fillStyle = accentColor + 'AA';
    ctx.fillText(content.tagline.toUpperCase(), pad, size.h - pad - base * 0.26 * 3);
  }
  drawBrandingBar(ctx, size, pad, textArea, base);
}


// ═══════════════════════════════════════════════════════
//  IMAGE LOADING + LOGO DRAWING
// ═══════════════════════════════════════════════════════

const imageCache = {};

function loadImage(src) {
  if (imageCache[src]) return Promise.resolve(imageCache[src]);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imageCache[src] = img; resolve(img); };
    img.onerror = () => reject(new Error(`Failed to load: ${src}`));
    img.src = src;
  });
}

function drawLogoOnCanvas(ctx, img, size, position, logoSize) {
  const pad = size.w * 0.06;
  const s = logoSize || size.w * 0.08;
  let x, y;
  switch (position) {
    case 'top-right':    x = size.w - pad - s; y = pad; break;
    case 'bottom-left':  x = pad; y = size.h - pad - s; break;
    case 'bottom-right': x = size.w - pad - s; y = size.h - pad - s; break;
    case 'top-left':
    default:             x = pad; y = pad; break;
  }
  // Draw a subtle semi-transparent circle behind the logo
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + s / 2, y + s / 2, s * 0.6, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fill();
  ctx.restore();
  ctx.drawImage(img, x, y, s, s);
}


// ═══════════════════════════════════════════════════════
//  REGISTRY
// ═══════════════════════════════════════════════════════

export const BACKGROUNDS = [
  { id: 'minimal',  name: 'Minimal Dark',  desc: 'Subtle grid with warm glow',    draw: drawBgMinimalDark },
  { id: 'ember',    name: 'Ember Forge',   desc: 'Molten lava with ember orbs',   draw: drawBgEmberForge },
  { id: 'circuit',  name: 'Circuit Pulse', desc: 'Tech circuits with neon nodes',  draw: drawBgCircuitPulse },
  { id: 'nebula',   name: 'Nebula Drift',  desc: 'Space nebula with shiny orbs',  draw: drawBgNebulaDrift },
  { id: 'matrix',   name: 'Grid Matrix',   desc: 'Perspective depth grid',        draw: drawBgGridMatrix },
  { id: 'aurora',   name: 'Aurora Wave',   desc: 'Flowing aurora bands',          draw: drawBgAuroraWave },
];

export const BASE_DESIGNS = [
  { id: 'classic',    name: 'Classic',        desc: 'Left-aligned, clean layout',     accentColor: '#E8722A', draw: layoutClassic },
  { id: 'hero',       name: 'Hero Statement', desc: 'Centered, bold headline',        accentColor: '#E8722A', draw: layoutHeroStatement },
  { id: 'editorial',  name: 'Editorial',      desc: 'Magazine-style, elegant spacing', accentColor: '#E8722A', draw: layoutEditorial },
  { id: 'boldstack',  name: 'Bold Stack',     desc: 'Tight uppercase with highlight bars', accentColor: '#FF7828', draw: layoutBoldStack },
  { id: 'cinematic',  name: 'Cinematic',      desc: 'Text at bottom, poster-style',   accentColor: '#E8722A', draw: layoutCinematic },
];

/**
 * Render a full post: background + text layout.
 * Background is ALWAYS randomized. Base design determines text composition.
 */
export function renderPost(ctx, size, bgId, designId, content) {
  const bg = BACKGROUNDS.find(b => b.id === bgId) || BACKGROUNDS[0];
  const design = BASE_DESIGNS.find(d => d.id === designId) || BASE_DESIGNS[0];
  bg.draw(ctx, size);
  design.draw(ctx, content, size, design.accentColor);
}

/**
 * Render a full post with logo support (async).
 * Draws bg + layout, then loads and draws logos if requested.
 */
export async function renderPostWithLogos(ctx, size, bgId, designId, content, backendUrl) {
  // First render the base post
  renderPost(ctx, size, bgId, designId, content);

  // Then draw logos if requested
  const logoPromises = [];

  if (content?.visualte_logo?.enabled) {
    const pos = content.visualte_logo.position || 'top-left';
    // Visualte logo is in the public assets
    logoPromises.push(
      loadImage('/assets/logos/visualte-logo.png')
        .then(img => drawLogoOnCanvas(ctx, img, size, pos, size.w * 0.08))
        .catch(err => console.warn('Failed to draw Visualte logo:', err))
    );
  }

  if (content?.project_logo?.enabled && content.project_logo.url) {
    const pos = content.project_logo.position || 'top-right';
    // Absolute URLs and site-bundled /assets/ paths load as-is; uploads live on the API.
    const logoUrl = /^https?:|^\/assets\//.test(content.project_logo.url)
      ? content.project_logo.url
      : (backendUrl || '') + content.project_logo.url;
    logoPromises.push(
      loadImage(logoUrl)
        .then(img => drawLogoOnCanvas(ctx, img, size, pos, size.w * 0.07))
        .catch(err => console.warn('Failed to draw project logo:', err))
    );
  }

  if (logoPromises.length > 0) {
    await Promise.allSettled(logoPromises);
  }
}

/**
 * Render a background-only thumbnail.
 */
export function renderBgThumbnail(ctx, size, bgId) {
  const bg = BACKGROUNDS.find(b => b.id === bgId) || BACKGROUNDS[0];
  bg.draw(ctx, size);
}

/**
 * Render a base design preview with sample text on a dark bg.
 */
export function renderDesignThumbnail(ctx, size, designId) {
  ctx.fillStyle = '#0A0D12';
  ctx.fillRect(0, 0, size.w, size.h);
  const design = BASE_DESIGNS.find(d => d.id === designId) || BASE_DESIGNS[0];
  const sample = { headline: 'Power Engine', body: 'Built for scale.', tagline: 'VISUALTE', accent_word: 'Power' };
  design.draw(ctx, sample, size, design.accentColor);
}
